// National / State / City architecture data.
// Each entry has UNIQUE content — no template duplication.
// capability:
//   "hub"        — Alpha has an active physical operations hub in this location
//   "serviced"   — Alpha delivers services via its national coordination model (subject to capacity)
//   "capability" — city sits within a serviced state; enquiries assessed case-by-case

export const STATES = {
  "victoria": {
    slug: "victoria",
    code: "VIC",
    name: "Victoria",
    capability: "hub",
    hubCity: "melbourne",
    lat: -37.4713,
    lon: 144.7852,
    tagline: "Where Alpha started — and where we still deliver the deepest support",
    intro:
      "Victoria is Alpha Community Care's home state. Our head-office team runs day-to-day operations out of Docklands, Melbourne — with active SIL homes, in-home supports, community nursing partnerships and support coordination delivered across metropolitan Melbourne and greater Victoria.",
    positioning:
      "We're one of the very few NDIS providers who combine SIL, SDA, forensic overlay and community nursing under a single Victorian operations team — with named coordinators and quarterly plan reviews.",
    services: [
      { slug: "supported-independent-living", label: "Supported Independent Living (SIL)", note: "Active SIL homes with 24/7 rostering, including forensic-overlay placements." },
      { slug: "specialist-disability-accommodation", label: "Specialist Disability Accommodation (SDA)", note: "SDA across all four design categories — Improved Liveability, Fully Accessible, Robust and High Physical Support." },
      { slug: "community-nursing-care", label: "Community Nursing", note: "Registered nurses partnering with SIL homes and in-home participants across greater Melbourne." },
      { slug: "support-coordination", label: "Support Coordination", note: "Named coordinators handling complex plans, hospital discharge and inter-provider negotiation." },
      { slug: "community-participation", label: "Community Participation", note: "1:1 and small-group community access — from swimming to work-readiness." },
      { slug: "forensic-disability-services", label: "Forensic Disability Services", note: "Trained forensic-overlay support workers, positive behaviour support and clinical governance for justice-involved participants." },
      { slug: "in-home-support", label: "In-Home Support", note: "Daily living, personal care and community access — anywhere in metro Melbourne." },
    ],
    cities: [
      { slug: "melbourne", label: "Melbourne", capability: "hub" },
      // Geelong intentionally excluded — awaiting management verification of local capability
    ],
    outsideMetro:
      "Beyond metropolitan Melbourne (including Geelong, Ballarat, Bendigo and regional Victoria), Alpha assesses referrals case-by-case based on workforce availability and clinical requirements.",
    referral:
      "Referrals from Victoria are triaged by our Docklands team within 2 business hours. For complex forensic or clinical placements, an intake clinician joins the discovery call.",
    faqs: [
      { q: "Does Alpha have SIL vacancies in Melbourne?", a: "Yes — our current SIL vacancies are published on the Our Homes page, including our Coburg and Aintree properties. Vacancies change weekly." },
      { q: "Can Alpha support participants outside metropolitan Melbourne?", a: "In many cases — yes. Regional Victoria is reviewed case-by-case based on workforce availability. Call intake on 1300 380 692 to discuss." },
      { q: "Is Alpha's Victorian team NDIS-registered?", a: "Yes. Alpha Community Care is a registered NDIS provider audited against the NDIS Practice Standards, delivering services under a single ABN across Victoria." },
      { q: "What forensic capacity does Alpha have in Victoria?", a: "Alpha's forensic-overlay SIL is a Victorian specialty. Our forensic team delivers 24/7 placements with positive behaviour support, clinical oversight and documented safeguarding." },
    ],
  },

  "western-australia": {
    slug: "western-australia",
    code: "WA",
    name: "Western Australia",
    capability: "hub",
    hubCity: "perth",
    lat: -31.9505,
    lon: 115.8605,
    tagline: "A dedicated WA team, delivering out of West Perth",
    intro:
      "Western Australia is Alpha Community Care's second operational base. Our West Perth team delivers SIL, SDA, community participation and coordination across the Perth metropolitan area — with clinical and forensic partnerships extending our reach across greater WA.",
    positioning:
      "In WA we've deliberately kept our footprint tight — enough capacity to deliver consistent support in Perth without spreading thin. Coordinators tell us the difference shows up in staff consistency and response time.",
    services: [
      { slug: "supported-independent-living", label: "Supported Independent Living (SIL)", note: "SIL placements delivered across the Perth metro area." },
      { slug: "specialist-disability-accommodation", label: "Specialist Disability Accommodation (SDA)", note: "SDA placements assessed against current portfolio availability in WA." },
      { slug: "support-coordination", label: "Support Coordination", note: "Perth-based coordinators handling complex NDIS plans and cross-service navigation." },
      { slug: "community-participation", label: "Community Participation", note: "Personalised community access supports across Perth suburbs." },
      { slug: "in-home-support", label: "In-Home Support", note: "Daily living, personal care and community access across the Perth metro area." },
      { slug: "community-nursing-care", label: "Community Nursing", note: "Community nursing available in WA subject to clinical scope — call intake to confirm current capacity." },
    ],
    cities: [
      { slug: "perth", label: "Perth", capability: "hub" },
    ],
    outsideMetro:
      "Beyond the Perth metropolitan area (Rockingham, Fremantle, Joondalup and regional WA), Alpha assesses referrals case-by-case. Regional WA is not part of our routine service area today.",
    referral:
      "WA referrals route to our West Perth intake, acknowledged within 2 business hours. Complex or cross-state placements involve our Victorian clinical team.",
    faqs: [
      { q: "Does Alpha have a Perth office?", a: "Yes — Alpha's West Perth operations base runs our WA service delivery, including support workers, coordinators and management." },
      { q: "What NDIS supports does Alpha deliver in WA today?", a: "SIL, community participation, support coordination and in-home support across Perth metro — with SDA and community nursing available subject to clinical scope and portfolio." },
      { q: "Can Alpha take crisis referrals in Perth?", a: "We assess crisis and priority referrals in WA case-by-case, including hospital discharge and safety-of-home scenarios. Call 1300 380 692." },
      { q: "Does Alpha support regional Western Australia?", a: "Regional WA sits outside our routine service area. We consider it case-by-case where workforce and clinical needs can be met." },
    ],
  },

  "new-south-wales": {
    slug: "new-south-wales",
    code: "NSW",
    name: "New South Wales",
    capability: "serviced",
    hubCity: null,
    lat: -33.8688,
    lon: 151.2093,
    tagline: "National intake, NSW-based referrals — assessed case-by-case",
    intro:
      "Alpha Community Care supports NSW participants and referral partners through our national intake model. We don't currently operate a NSW office — instead we assess referrals against clinical scope, workforce availability and (where relevant) accommodation.",
    positioning:
      "For coordinators referring interstate or hospital discharge teams seeking complex placements, we can help — provided we can genuinely support the participant. When we can't, we say so.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Remote and hybrid coordination for NSW participants with complex plans." },
      { slug: "supported-independent-living", label: "SIL referrals", note: "SIL referrals from NSW are considered where cross-state placement is appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Forensic-overlay referrals with cross-state placement considered on a case-by-case basis." },
      { slug: "community-nursing-care", label: "Community Nursing consultation", note: "Consultation for participants transitioning between jurisdictions." },
    ],
    cities: [
      { slug: "sydney", label: "Sydney", capability: "capability" },
      // Newcastle, Wollongong intentionally excluded — awaiting management confirmation
    ],
    outsideMetro:
      "Beyond Sydney (Newcastle, the Illawarra, the Central Coast, regional NSW), enquiries are assessed against workforce and clinical requirements before we commit.",
    referral:
      "NSW referrals are handled by our intake team over phone and video. Interstate placement into our VIC or WA services is possible where appropriate.",
    faqs: [
      { q: "Does Alpha Community Care have an office in Sydney?", a: "No — Alpha's active hubs are Melbourne and Perth. NSW enquiries are supported through our national intake team." },
      { q: "Can Alpha place a NSW participant into a Melbourne SIL home?", a: "Yes, where the participant, family and coordinator agree it's the right fit and where our home has genuine vacancy. Cross-state placement decisions are made carefully." },
      { q: "How do NSW coordinators refer to Alpha?", a: "Use our coordinator referral form or call 1300 380 692. We'll acknowledge within 2 business hours and confirm whether we can genuinely support your participant." },
      { q: "Does Alpha take forensic referrals from NSW?", a: "Forensic referrals from NSW are considered where cross-state placement into our Victorian forensic-overlay SIL is appropriate. Not every referral is a fit." },
    ],
  },

  "queensland": {
    slug: "queensland",
    code: "QLD",
    name: "Queensland",
    capability: "serviced",
    hubCity: null,
    lat: -27.4698,
    lon: 153.0251,
    tagline: "National support model — Queensland referrals reviewed case-by-case",
    intro:
      "Alpha Community Care doesn't operate a physical hub in Queensland. We support Queensland enquiries — from coordinators, families and hospital discharge teams — through our national intake model, focusing on referrals that suit our specialist services.",
    positioning:
      "Where we can add genuine value — typically complex placements, forensic considerations or cross-state SIL — we help. For everyday supports, a local Queensland provider is often the better answer.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Considered on a case-by-case basis for participants with complex or interstate plans." },
      { slug: "supported-independent-living", label: "SIL referrals", note: "Interstate SIL placement into Alpha's VIC/WA homes considered where appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Forensic overlay referrals from QLD considered case-by-case." },
    ],
    cities: [
      { slug: "brisbane", label: "Brisbane", capability: "capability" },
      // Gold Coast, Sunshine Coast, Townsville, Cairns, Bundaberg — awaiting verification
    ],
    outsideMetro:
      "Regional Queensland (Cairns, Townsville, Sunshine Coast, Gold Coast, Bundaberg) sits outside our routine service area. Enquiries are assessed against clinical and workforce feasibility.",
    referral:
      "Queensland referrals route through our national intake team and are acknowledged within 2 business hours. Complex referrals include a discovery call with a clinician.",
    faqs: [
      { q: "Does Alpha service Brisbane?", a: "We don't operate a Brisbane office. We assess Brisbane referrals through our national intake model — usually for participants with specialist or interstate needs." },
      { q: "Can I refer a Queensland participant to Alpha's Victorian SIL homes?", a: "Yes. Cross-state SIL placement is possible where the home is a genuine fit and where the participant, family and coordinator support the move." },
      { q: "Does Alpha have SIL vacancies in Queensland?", a: "No — Alpha's SIL portfolio is currently based in Victoria and Western Australia." },
      { q: "How do Queensland hospitals refer complex disability discharges to Alpha?", a: "Call 1300 380 692 or email intake@alphacommunitycare.com.au with the discharge summary. We assess quickly and honestly." },
    ],
  },

  "south-australia": {
    slug: "south-australia",
    code: "SA",
    name: "South Australia",
    capability: "serviced",
    hubCity: null,
    lat: -34.9285,
    lon: 138.6007,
    tagline: "Interstate capability from Adelaide — not a duplicated local footprint",
    intro:
      "South Australia is not one of Alpha's operational hubs. We take enquiries from Adelaide-based coordinators and families through our national intake, with a focus on specialist referrals that benefit from our Victorian and WA capability.",
    positioning:
      "We'd rather say honestly that a Melbourne-based specialist service isn't right for every Adelaide participant, than pretend to be a local provider. Where cross-state SIL, forensic or complex placements make sense, we can help.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Complex plan coordination considered for SA participants case-by-case." },
      { slug: "supported-independent-living", label: "SIL referrals", note: "Cross-state SIL placement into our VIC or WA portfolio where clinically and personally appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Considered on a case-by-case basis where our forensic-overlay capability is a genuine fit." },
    ],
    cities: [
      { slug: "adelaide", label: "Adelaide", capability: "capability" },
    ],
    outsideMetro:
      "Regional South Australia (Mount Gambier, Port Augusta, the Fleurieu) sits outside our routine service area. We're transparent about that up front.",
    referral:
      "SA referrals are triaged by our national intake team and acknowledged within 2 business hours. Complex placements involve our clinical lead.",
    faqs: [
      { q: "Does Alpha have an Adelaide office?", a: "No — our active hubs are Melbourne and Perth. Adelaide enquiries are supported through national intake and typically involve interstate SIL, forensic or complex referrals." },
      { q: "Can Alpha coordinate a SA participant's plan remotely?", a: "In some cases — typically where the plan involves interstate services or complex specialist inputs. We're honest when a local SA coordinator is a better fit." },
      { q: "Is Alpha NDIS-registered in South Australia?", a: "Alpha is a registered NDIS provider under a single national registration. Registration applies wherever we deliver services." },
    ],
  },

  "tasmania": {
    slug: "tasmania",
    code: "TAS",
    name: "Tasmania",
    capability: "serviced",
    hubCity: null,
    lat: -42.8821,
    lon: 147.3272,
    tagline: "Tasmanian referrals — reviewed case-by-case, mainland-based delivery",
    intro:
      "Alpha Community Care does not deliver ongoing support services on the ground in Tasmania. We consider Tasmanian referrals for interstate SIL placements, complex plan coordination and forensic overlay where our Victorian or WA services can genuinely help.",
    positioning:
      "If your Tasmanian participant is a strong candidate for interstate specialist SIL, forensic-overlay support or complex coordination, we can help. Otherwise, a local Tasmanian provider is usually the better route.",
    services: [
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Cross-state SIL placement into VIC or WA homes considered case-by-case." },
      { slug: "support-coordination", label: "Support Coordination", note: "Remote coordination for participants with interstate service needs." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Justice-involved participant referrals considered case-by-case." },
    ],
    cities: [
      { slug: "hobart", label: "Hobart", capability: "capability" },
    ],
    outsideMetro:
      "Regional Tasmania (Launceston, the north-west coast, Devonport) — please assume we cannot deliver ongoing local services and enquire only for specialist interstate placements.",
    referral:
      "Tasmanian referrals are handled by our national intake team via phone and video. Complex assessments involve our clinical team.",
    faqs: [
      { q: "Does Alpha operate in Tasmania?", a: "Not on the ground. We support Tasmanian referrals through our national intake for interstate specialist placements — not ongoing local services." },
      { q: "Can a Tasmanian participant move into an Alpha SIL home in Melbourne?", a: "Yes, where the placement genuinely suits the participant and their coordinator, family and existing team support the move. Interstate placement decisions are made carefully." },
    ],
  },

  "australian-capital-territory": {
    slug: "australian-capital-territory",
    code: "ACT",
    name: "Australian Capital Territory",
    capability: "serviced",
    hubCity: null,
    lat: -35.2809,
    lon: 149.1300,
    tagline: "ACT referrals — assessed nationally, delivered where appropriate",
    intro:
      "Alpha Community Care does not maintain a Canberra base. We assess ACT enquiries through our national intake team, focusing on specialist or interstate placements that suit our operational model.",
    positioning:
      "For Canberra coordinators managing participants with complex or interstate needs, we can help assess whether Alpha's Melbourne or Perth services are a genuine fit.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Complex plan coordination considered for ACT participants case-by-case." },
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Considered where the placement is a clinical, personal and family-supported fit." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Considered case-by-case." },
    ],
    cities: [
      { slug: "canberra", label: "Canberra", capability: "capability" },
    ],
    outsideMetro:
      "Beyond Canberra (Queanbeyan, Belconnen and surrounding areas) we assess enquiries against the same national intake criteria.",
    referral:
      "ACT referrals are handled remotely by our national intake team and acknowledged within 2 business hours.",
    faqs: [
      { q: "Does Alpha have a Canberra office?", a: "No. Canberra enquiries are supported through Alpha's national intake team. Our physical hubs are Melbourne and Perth." },
      { q: "Does Alpha do NDIS Support Coordination in the ACT?", a: "In selected cases — typically where the participant's plan involves interstate services or specialist supports. We're upfront when a local coordinator is the better fit." },
    ],
  },

  "northern-territory": {
    slug: "northern-territory",
    code: "NT",
    name: "Northern Territory",
    capability: "serviced",
    hubCity: null,
    lat: -12.4634,
    lon: 130.8456,
    tagline: "Northern Territory enquiries — considered for specialist interstate placements only",
    intro:
      "Alpha Community Care does not operate on the ground in the Northern Territory. NT enquiries are considered case-by-case for specialist interstate placements — typically SIL, forensic overlay or complex coordination that benefits from our Victorian services.",
    positioning:
      "We won't pretend to service Darwin locally. Where an NT participant is a genuine candidate for our interstate specialist services, we're happy to help assess.",
    services: [
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Cross-state placement considered where clinically and personally appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Justice-involved participant referrals considered case-by-case." },
    ],
    cities: [
      { slug: "darwin", label: "Darwin", capability: "capability" },
    ],
    outsideMetro:
      "Beyond Darwin (Palmerston, Alice Springs, remote NT) — we don't deliver on-the-ground services. Please enquire only for specialist interstate needs.",
    referral:
      "NT referrals are handled by our national intake team via phone and video, with clinical involvement for complex assessments.",
    faqs: [
      { q: "Does Alpha service Darwin?", a: "Not locally. Alpha's active hubs are Melbourne and Perth. We consider Darwin enquiries only where a specialist interstate placement is a genuine fit." },
      { q: "Can Alpha provide Community Nursing in the NT?", a: "Not currently. Community Nursing is delivered in our Melbourne and Perth service areas." },
    ],
  },
};

// City-level content — only for cities where we've confirmed enough content and capability
// to justify a dedicated page. Others are surfaced within the state page.
export const CITIES = {
  "melbourne": {
    slug: "melbourne",
    stateSlug: "victoria",
    name: "Melbourne",
    fullName: "Melbourne, Victoria",
    capability: "hub",
    lat: -37.8136,
    lon: 144.9631,
    postalCode: "3008",
    suburb: "Docklands",
    tagline: "Alpha's head office — the deepest disability support footprint we operate",
    intro:
      "Melbourne is Alpha Community Care's headquarters. From our Docklands operations base we deliver SIL, SDA, community nursing, support coordination and forensic-overlay services across the metropolitan area — including our two flagship SIL homes in Coburg and Aintree.",
    hubDetails: {
      address: "Docklands, Melbourne VIC 3008",
      teamSize: "Head office team — operations, clinical, forensic and coordination",
      responseTime: "Within 2 business hours (Mon–Fri)",
    },
    services: [
      { slug: "supported-independent-living", label: "Supported Independent Living (SIL)", note: "Active SIL homes including Coburg Retreat and Aintree House — with forensic-overlay placements." },
      { slug: "specialist-disability-accommodation", label: "Specialist Disability Accommodation (SDA)", note: "SDA across all four design categories in our Melbourne portfolio." },
      { slug: "community-nursing-care", label: "Community Nursing", note: "Registered nurses partnering with SIL homes and in-home participants across Melbourne." },
      { slug: "support-coordination", label: "Support Coordination", note: "Complex plan coordination and cross-provider negotiation, in-person where required." },
      { slug: "community-participation", label: "Community Participation", note: "1:1 and small group community access across greater Melbourne." },
      { slug: "forensic-disability-services", label: "Forensic Disability Services", note: "Alpha's forensic overlay is a Melbourne specialty — trained workforce, PBS and clinical governance." },
      { slug: "in-home-support", label: "In-Home Support", note: "Daily living, personal care and community access anywhere in Melbourne metro." },
    ],
    servicedSuburbs: [
      "Docklands", "Melbourne CBD", "Southbank", "Coburg", "Aintree", "Brunswick", "Footscray", "Richmond", "Northcote", "Preston",
      "Sunshine", "Werribee", "Point Cook", "Essendon", "Moonee Ponds", "Box Hill", "Doncaster", "Ringwood", "Dandenong", "Frankston",
    ],
    homes: [
      { slug: "coburg-retreat", label: "Coburg Retreat", suburb: "Coburg" },
      { slug: "aintree-house", label: "Aintree House", suburb: "Aintree" },
    ],
    proximity:
      "Our SIL homes sit within 20 minutes of the Royal Melbourne Hospital, the Austin, St Vincent's and a network of allied-health specialists — with easy tram and rail access to the CBD and community hubs.",
    referral:
      "Melbourne referrals are triaged by our Docklands team within 2 business hours. Complex placements involve our clinical or forensic lead in the discovery call.",
    faqs: [
      { q: "Where is Alpha's Melbourne office?", a: "Alpha Community Care's Melbourne operations run from Docklands, VIC 3008. Meetings can be arranged at Docklands or in-community by appointment." },
      { q: "Which Melbourne SIL vacancies does Alpha have?", a: "Current SIL vacancies are published on our Our Homes page — including Coburg Retreat and Aintree House. Availability changes weekly." },
      { q: "How quickly can Alpha respond to a Melbourne referral?", a: "We acknowledge within 2 business hours, with capacity confirmed inside 48 hours. Crisis discharge referrals are prioritised." },
      { q: "Does Alpha have forensic SIL in Melbourne?", a: "Yes. Alpha's forensic-overlay SIL is delivered from our Melbourne base with specifically-trained staff, PBS and clinical oversight." },
    ],
  },

  "perth": {
    slug: "perth",
    stateSlug: "western-australia",
    name: "Perth",
    fullName: "Perth, Western Australia",
    capability: "hub",
    lat: -31.9505,
    lon: 115.8605,
    postalCode: "6005",
    suburb: "West Perth",
    tagline: "Deliberately tight, deliberately consistent — WA's West Perth delivery hub",
    intro:
      "Perth is Alpha Community Care's second operational base. From West Perth we deliver SIL, community participation, support coordination and in-home supports across the Perth metropolitan area — with clinical and forensic partnerships extending our reach when needed.",
    hubDetails: {
      address: "West Perth, WA 6005",
      teamSize: "Perth delivery team — support workers, coordinators and management",
      responseTime: "Within 2 business hours (Mon–Fri)",
    },
    services: [
      { slug: "supported-independent-living", label: "Supported Independent Living (SIL)", note: "SIL placements delivered across Perth metro." },
      { slug: "support-coordination", label: "Support Coordination", note: "Complex plan coordination and cross-provider negotiation with a Perth-based lead." },
      { slug: "community-participation", label: "Community Participation", note: "Personalised community access supports across Perth suburbs." },
      { slug: "in-home-support", label: "In-Home Support", note: "Daily living, personal care and community access across the Perth metro area." },
      { slug: "specialist-disability-accommodation", label: "SDA (subject to portfolio)", note: "SDA placements assessed against current portfolio availability in WA." },
      { slug: "community-nursing-care", label: "Community Nursing (case-by-case)", note: "Community nursing available in WA subject to clinical scope — call intake to confirm current capacity." },
    ],
    servicedSuburbs: [
      "West Perth", "Perth CBD", "Northbridge", "Subiaco", "Nedlands", "Cottesloe", "Claremont",
      "Joondalup", "Wanneroo", "Morley", "Bassendean", "Fremantle", "Cannington", "Rockingham",
    ],
    homes: [],
    proximity:
      "Our Perth operations sit within easy reach of Royal Perth Hospital, Sir Charles Gairdner and a network of Perth allied-health providers — with train and bus access across the metro area.",
    referral:
      "Perth referrals are triaged by our West Perth team within 2 business hours. Cross-state or clinically complex placements involve our national clinical team.",
    faqs: [
      { q: "Where is Alpha's Perth office?", a: "Alpha Community Care's Perth operations run from West Perth, WA 6005. Meetings can be arranged in West Perth or in-community by appointment." },
      { q: "What does Alpha deliver in Perth today?", a: "SIL, community participation, support coordination and in-home support across Perth metro — with SDA and community nursing available subject to portfolio and clinical scope." },
      { q: "Does Alpha take Perth crisis referrals?", a: "We assess crisis and priority referrals — including hospital discharge and safety-of-home scenarios — case-by-case. Call 1300 380 692." },
      { q: "Does Alpha service regional WA?", a: "Regional WA is outside our routine service area. We consider it case-by-case where workforce and clinical requirements can be met." },
    ],
  },

  "sydney": {
    slug: "sydney",
    stateSlug: "new-south-wales",
    name: "Sydney",
    fullName: "Sydney, New South Wales",
    capability: "capability",
    lat: -33.8688,
    lon: 151.2093,
    postalCode: null,
    suburb: null,
    tagline: "National intake, Sydney referrals — assessed against genuine fit",
    intro:
      "Alpha Community Care doesn't run a Sydney office. Sydney enquiries — from participants, families, coordinators and hospital discharge teams — are supported through our national intake team, with a bias towards specialist or interstate referrals where our Melbourne and Perth capability makes a genuine difference.",
    positioning:
      "For Sydney-based coordinators managing complex, forensic or interstate-move placements, we can help. For everyday supports in Sydney metro, a local NSW provider is usually the right answer — and we'll tell you so.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Complex or interstate coordination for Sydney participants — case-by-case." },
      { slug: "supported-independent-living", label: "SIL referrals", note: "Cross-state SIL placement into Alpha's Melbourne homes considered where genuinely appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Considered where cross-state placement into our forensic-overlay SIL is a fit." },
    ],
    servicedSuburbs: [],
    homes: [],
    referral:
      "Sydney referrals reach our national intake team via phone and email — acknowledged within 2 business hours, with honest capacity feedback.",
    faqs: [
      { q: "Does Alpha Community Care have an office in Sydney?", a: "No — Alpha's active hubs are Melbourne and Perth. Sydney enquiries are supported through our national intake team." },
      { q: "Can Alpha place a Sydney participant into a Melbourne SIL home?", a: "Yes, where the placement is a genuine fit for the participant, and the family and coordinator support the move. Cross-state placement decisions are made carefully." },
      { q: "How do Sydney coordinators refer to Alpha?", a: "Use our coordinator referral form or call 1300 380 692. We'll acknowledge within 2 business hours and confirm honestly whether we can help." },
      { q: "Does Alpha have SIL vacancies in Sydney?", a: "No — Alpha's SIL portfolio is currently based in Victoria and Western Australia." },
    ],
  },

  "brisbane": {
    slug: "brisbane",
    stateSlug: "queensland",
    name: "Brisbane",
    fullName: "Brisbane, Queensland",
    capability: "capability",
    lat: -27.4698,
    lon: 153.0251,
    postalCode: null,
    suburb: null,
    tagline: "Brisbane referrals routed through national intake — specialist placements only",
    intro:
      "Alpha Community Care doesn't operate in Brisbane on the ground. Brisbane referrals — from Queensland coordinators, discharge planners and families — are considered through our national intake team when a specialist or cross-state placement suits our Melbourne or Perth capability.",
    positioning:
      "We're not the right provider for everyday Brisbane supports. We can genuinely help with specialist SIL, forensic overlay or complex cross-state placements — and we're upfront when we can't.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Considered case-by-case for Brisbane participants with complex or interstate plans." },
      { slug: "supported-independent-living", label: "SIL referrals", note: "Interstate SIL placement into our Victorian homes considered where appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Considered case-by-case." },
    ],
    servicedSuburbs: [],
    homes: [],
    referral:
      "Brisbane referrals are triaged by our national intake within 2 business hours, with clinical involvement for complex assessments.",
    faqs: [
      { q: "Does Alpha service Brisbane locally?", a: "We don't operate a Brisbane office. We assess Brisbane referrals through our national intake — usually for specialist or interstate placements." },
      { q: "Can a Brisbane participant move into an Alpha SIL home in Melbourne?", a: "Yes, if the placement is a genuine fit and everyone involved (participant, family, coordinator) supports the move." },
      { q: "Does Alpha have SIL vacancies in Brisbane?", a: "No — our SIL portfolio is in Victoria and WA." },
    ],
  },

  "adelaide": {
    slug: "adelaide",
    stateSlug: "south-australia",
    name: "Adelaide",
    fullName: "Adelaide, South Australia",
    capability: "capability",
    lat: -34.9285,
    lon: 138.6007,
    postalCode: null,
    suburb: null,
    tagline: "Adelaide enquiries — specialist referrals only, not a local footprint",
    intro:
      "Alpha Community Care doesn't run an Adelaide office. Adelaide enquiries are handled through our national intake, focusing on specialist interstate placements and complex coordination where our Melbourne services can genuinely add value.",
    positioning:
      "For local Adelaide supports, an SA-based NDIS provider is usually the right call. Where Alpha's Melbourne SIL, forensic-overlay or complex coordination is a genuine fit, we can help.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Complex plan coordination considered for SA participants case-by-case." },
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Cross-state SIL placement into Alpha's Victorian portfolio where genuinely appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Considered on a case-by-case basis." },
    ],
    servicedSuburbs: [],
    homes: [],
    referral:
      "Adelaide referrals are handled remotely by our national intake team and acknowledged within 2 business hours.",
    faqs: [
      { q: "Does Alpha have an Adelaide office?", a: "No — our active hubs are Melbourne and Perth. Adelaide enquiries are supported by our national intake team." },
      { q: "Can Alpha do NDIS Support Coordination in Adelaide?", a: "In some cases — typically where the participant's plan involves interstate services or specialist supports. We're honest when a local SA coordinator is a better fit." },
    ],
  },

  "hobart": {
    slug: "hobart",
    stateSlug: "tasmania",
    name: "Hobart",
    fullName: "Hobart, Tasmania",
    capability: "capability",
    lat: -42.8821,
    lon: 147.3272,
    postalCode: null,
    suburb: null,
    tagline: "Hobart referrals — considered for specialist interstate placements only",
    intro:
      "Alpha Community Care doesn't operate locally in Hobart. We assess Hobart enquiries case-by-case through our national intake, focusing on specialist referrals where cross-state placement into our Victorian services genuinely helps the participant.",
    positioning:
      "We won't pretend to service Hobart on the ground. Where an interstate placement or complex coordination is a real fit, we're happy to help.",
    services: [
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Cross-state placement into Alpha's Melbourne SIL considered case-by-case." },
      { slug: "support-coordination", label: "Support Coordination", note: "Remote coordination for participants with interstate service needs." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Justice-involved referrals considered case-by-case." },
    ],
    servicedSuburbs: [],
    homes: [],
    referral:
      "Hobart referrals are handled by our national intake team via phone and video, with a clinical join-in on complex assessments.",
    faqs: [
      { q: "Does Alpha operate in Hobart?", a: "Not locally. We support Hobart referrals through our national intake for interstate specialist placements — not everyday supports." },
      { q: "Can a Hobart participant move into a Melbourne SIL home with Alpha?", a: "Yes — where the move is a genuine fit and their coordinator and family agree." },
    ],
  },

  "canberra": {
    slug: "canberra",
    stateSlug: "australian-capital-territory",
    name: "Canberra",
    fullName: "Canberra, ACT",
    capability: "capability",
    lat: -35.2809,
    lon: 149.1300,
    postalCode: null,
    suburb: null,
    tagline: "Canberra referrals — assessed nationally, delivered where appropriate",
    intro:
      "Alpha Community Care doesn't maintain a Canberra hub. We assess Canberra enquiries through our national intake, focusing on specialist referrals — typically complex plan coordination or interstate SIL placement — where our Melbourne and Perth capability genuinely helps.",
    positioning:
      "For everyday Canberra supports, a local ACT provider is usually the right answer. For specialist or interstate needs, our national intake can help assess whether Alpha is a fit.",
    services: [
      { slug: "support-coordination", label: "Support Coordination", note: "Complex plan coordination considered for ACT participants case-by-case." },
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Considered where the placement is clinically and personally appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Considered case-by-case." },
    ],
    servicedSuburbs: [],
    homes: [],
    referral:
      "Canberra referrals are handled remotely by our national intake team and acknowledged within 2 business hours.",
    faqs: [
      { q: "Does Alpha have a Canberra office?", a: "No. Canberra enquiries are supported through Alpha's national intake team. Our physical hubs are Melbourne and Perth." },
      { q: "Does Alpha do NDIS Support Coordination in the ACT?", a: "In selected cases — typically where the participant's plan involves interstate services. We're upfront when a local coordinator is the better fit." },
    ],
  },

  "darwin": {
    slug: "darwin",
    stateSlug: "northern-territory",
    name: "Darwin",
    fullName: "Darwin, Northern Territory",
    capability: "capability",
    lat: -12.4634,
    lon: 130.8456,
    postalCode: null,
    suburb: null,
    tagline: "Darwin enquiries — specialist interstate referrals only",
    intro:
      "Alpha Community Care doesn't deliver ongoing services in Darwin. We consider Darwin enquiries case-by-case for specialist interstate placements — typically SIL, forensic overlay or complex coordination that benefits from our Melbourne capability.",
    positioning:
      "We won't pretend to service Darwin locally. Where an NT participant is a real candidate for our interstate specialist services, we're happy to help assess.",
    services: [
      { slug: "supported-independent-living", label: "Interstate SIL placement", note: "Cross-state placement into Alpha's Melbourne SIL considered where appropriate." },
      { slug: "forensic-disability-services", label: "Forensic Disability referrals", note: "Justice-involved referrals considered case-by-case." },
    ],
    servicedSuburbs: [],
    homes: [],
    referral:
      "Darwin referrals are handled by our national intake via phone and video, with clinical involvement for complex assessments.",
    faqs: [
      { q: "Does Alpha service Darwin?", a: "Not locally. Alpha's active hubs are Melbourne and Perth. We consider Darwin enquiries only for specialist interstate placements." },
      { q: "Can Alpha provide Community Nursing in the NT?", a: "Not currently. Community Nursing is delivered in our Melbourne and Perth service areas." },
    ],
  },
};

// Cities flagged for management verification — NOT auto-created.
// Add to CITIES only after confirming genuine service capability.
export const CITIES_PENDING_VERIFICATION = [
  { name: "Geelong", stateSlug: "victoria" },
  { name: "Newcastle", stateSlug: "new-south-wales" },
  { name: "Wollongong", stateSlug: "new-south-wales" },
  { name: "Gold Coast", stateSlug: "queensland" },
  { name: "Sunshine Coast", stateSlug: "queensland" },
  { name: "Townsville", stateSlug: "queensland" },
  { name: "Cairns", stateSlug: "queensland" },
  { name: "Bundaberg", stateSlug: "queensland" },
  { name: "Launceston", stateSlug: "tasmania" },
  { name: "Palmerston", stateSlug: "northern-territory" },
];

export const STATE_SLUGS = Object.keys(STATES);
export const CITY_SLUGS = Object.keys(CITIES);
